import CheckCircleIcon from "@mui/icons-material/CheckCircle"
import ErrorIcon from "@mui/icons-material/Error"
import HelpIcon from "@mui/icons-material/Help"
import {
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
} from "@mui/material"
import {
  FC,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react"
import { useTranslation } from "react-i18next"
import { useNotification } from "src/components/NotificationProvider"
import { Sl2Btn } from "src/components/Sl2Btn"
import { ModeContext } from "src/ModeContext"
import { PrinterStatus, queryPrinterStatus } from "./browserPrint"
import { checkBrowserPrintHealth } from "./browserPrintHealth"
import { BrowserPrintSetupSteps } from "./BrowserPrintSetupSteps"
import { BrowserPrintStatusIcon } from "./BrowserPrintStatusIcon"
import { printErrorMessageKey } from "./printErrors"
import { useDepositCodePrinting } from "./useDepositCodePrinting"

type BrowserPrintSetupDialogProps = {
  tenantId?: string
  onClose: () => void
}

type CheckState = boolean | null

const CheckRow: FC<{ ok: CheckState; label: ReactNode }> = ({ ok, label }) => (
  <Stack direction="row" spacing={1} alignItems="center">
    {ok === true ? (
      <CheckCircleIcon color="success" fontSize="small" />
    ) : ok === false ? (
      <ErrorIcon color="error" fontSize="small" />
    ) : (
      <HelpIcon color="disabled" fontSize="small" />
    )}
    <Typography variant="body2">{label}</Typography>
  </Stack>
)

/**
 * LOCKER-522 — FR/EN install guide plus a live validation: is the agent
 * reachable, is a default printer set, does the printer report ready, and a
 * test label. Mirrors `apps/sl2/docs/zebra-browser-print-setup.md`.
 */
export const BrowserPrintSetupDialog: FC<BrowserPrintSetupDialogProps> = ({
  tenantId,
  onClose,
}) => {
  const { t } = useTranslation()
  const { showNotification } = useNotification()
  const mode = useContext(ModeContext)
  const { health, printSample } = useDepositCodePrinting(tenantId)
  const [printerStatus, setPrinterStatus] = useState<PrinterStatus | null>()
  const [isChecking, setIsChecking] = useState(false)
  const [isPrinting, setIsPrinting] = useState(false)

  const runChecks = useCallback(async () => {
    setIsChecking(true)
    setPrinterStatus(undefined)
    try {
      const next = await checkBrowserPrintHealth()
      if (next.status === "ready" && next.printer) {
        setPrinterStatus(
          await queryPrinterStatus(next.printer).catch(() => null),
        )
      }
    } finally {
      setIsChecking(false)
    }
  }, [])

  useEffect(() => {
    void runChecks()
  }, [runChecks])

  const onTestPrint = async () => {
    setIsPrinting(true)
    try {
      await printSample()
      showNotification(t("browserPrintTestSent"), "success")
    } catch (err) {
      showNotification(t(printErrorMessageKey(err)), "error")
    } finally {
      setIsPrinting(false)
    }
  }

  const agentOk: CheckState =
    health.status === "checking" ? null : health.status !== "unavailable"
  const printerOk: CheckState =
    health.status === "checking" || health.status === "unavailable"
      ? null
      : health.status === "ready"
  const readyOk: CheckState =
    printerStatus === undefined || printerStatus === null
      ? null
      : printerStatus.isReadyToPrint

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="md"
      fullWidth
      data-ui="UI-SL2W-292"
    >
      <DialogTitle>
        <Stack direction="row" spacing={1} alignItems="center">
          <span>{t("browserPrintSetupTitle")}</span>
          <BrowserPrintStatusIcon health={health} />
        </Stack>
      </DialogTitle>
      <Divider />
      <DialogContent>
        <Stack spacing={3}>
          <Typography variant="body2">{t("browserPrintSetupIntro")}</Typography>
          {["embedded", "RxPro"].includes(mode) && (
            <Alert severity="info">{t("browserPrintEmbeddedNote")}</Alert>
          )}
          <BrowserPrintSetupSteps />
          <Divider />
          <Stack spacing={1}>
            <Typography variant="subtitle2">
              {t("browserPrintValidationTitle")}
            </Typography>
            <CheckRow ok={agentOk} label={t("browserPrintCheckAgent")} />
            <CheckRow
              ok={printerOk}
              label={
                health.printer?.name
                  ? t("browserPrintCheckPrinterNamed", {
                      name: health.printer.name,
                    })
                  : t("browserPrintCheckPrinter")
              }
            />
            <CheckRow
              ok={readyOk}
              label={
                printerStatus && printerStatus.errors.length > 0
                  ? printerStatus.errors
                      .map((e) => t(`zebraPrinterError.${e}`))
                      .join(", ")
                  : printerStatus === null
                    ? t("browserPrintCheckReadyUnknown")
                    : t("browserPrintCheckReady")
              }
            />
            <Typography variant="caption" color="text.secondary">
              {t("browserPrintValidationHint")}
            </Typography>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Sl2Btn onClick={runChecks} disabled={isChecking}>
          {t("browserPrintCheckAgain")}
        </Sl2Btn>
        <Sl2Btn
          variant="outlined"
          onClick={onTestPrint}
          disabled={isPrinting || health.status !== "ready"}
        >
          {t("browserPrintTestPrint")}
        </Sl2Btn>
        <Sl2Btn variant="contained" onClick={onClose}>
          {t("close")}
        </Sl2Btn>
      </DialogActions>
    </Dialog>
  )
}
